import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiArrowRight,
  FiBookOpen,
  FiCalendar,
  FiCheckCircle,
  FiClipboard,
  FiGlobe,
  FiMic,
  FiShield,
  FiStar,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import AuthModal from "../pages/AuthModal.jsx";
import logo from "../assets/logo2.png";
import { useAuth } from "../context/AuthContext.jsx";
import {
  fetchMyOrganizerApplication,
  isApproved,
  isSuperadmin,
} from "../organizer/organizerApplication.js";

const HOST_TYPES = [
  { name: "Performances", copy: "Concerts, comedy, theatre and dance.", icon: FiMic, emoji: "🎭" },
  { name: "Experiences", copy: "Workshops, tours and immersive moments.", icon: FiActivity, emoji: "🎨" },
  { name: "Expositions", copy: "Art, culture, trade and showcase events.", icon: FiBookOpen, emoji: "🖼️" },
  { name: "Parties", copy: "Celebrations, nightlife and social gatherings.", icon: FiUsers, emoji: "🎉" },
  { name: "Sports", copy: "Matches, races, leagues and tournaments.", icon: FiGlobe, emoji: "⚽" },
  { name: "Conferences", copy: "Summits, talks and professional meetups.", icon: FiCalendar, emoji: "🎤" },
];

const HIGHLIGHTS = [
  {
    icon: FiZap,
    title: "Go live fast",
    copy: "Create your listing in minutes — we review and publish it for you.",
    gradient: "from-amber-400 to-orange-500",
  },
  {
    icon: FiShield,
    title: "Secure payments",
    copy: "Online ticketing with trusted payment processing and instant e-tickets.",
    gradient: "from-emerald-400 to-teal-500",
  },
  {
    icon: FiTrendingUp,
    title: "Track everything",
    copy: "Live sales, revenue and attendee analytics on your own dashboard.",
    gradient: "from-violet-400 to-purple-600",
  },
];

const STEPS = [
  {
    icon: FiClipboard,
    title: "Verify your account",
    copy: "Share your organisation, bank and document details once.",
    step: "01",
  },
  {
    icon: FiCalendar,
    title: "Create your event",
    copy: "Add venue, tickets, showtimes and images with our guided form.",
    step: "02",
  },
  {
    icon: FiCheckCircle,
    title: "Get approved & sell",
    copy: "Our team reviews your event and it goes live on ShowTimeBro.",
    step: "03",
  },
];

export default function ListYourShowLandingPage() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [authOpen, setAuthOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const goAfterAuth = "/listyourshow/account-setup";

  const handleListYourShow = async () => {
    if (!user?.token) {
      setAuthOpen(true);
      return;
    }
    if (isSuperadmin()) {
      nav("/listyourshow/manage");
      return;
    }
    setChecking(true);
    try {
      const app = await fetchMyOrganizerApplication();
      if (isApproved(app)) {
        nav("/listyourshow/manage");
      } else {
        nav(goAfterAuth);
      }
    } catch {
      nav(goAfterAuth);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    document.title = "List Your Show | ShowTimeBro";
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animated-gradient-text {
          background: linear-gradient(270deg, #fbbf24, #f97316, #ef4444, #fbbf24);
          background-size: 300% 300%;
          animation: gradient-shift 4s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Premium Scroll Reveal Animation Classes */
        .reveal-on-scroll {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-on-scroll.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Stagger Delays */
        .delay-100 { transition-delay: 100ms; }
        .delay-200 { transition-delay: 200ms; }
        .delay-300 { transition-delay: 300ms; }
        .delay-400 { transition-delay: 400ms; }
        .delay-500 { transition-delay: 500ms; }
        .delay-600 { transition-delay: 600ms; }
      `}</style>

      <div className="font-sans">

        {/* ─── HERO ─── */}
        <section className="relative min-h-[92vh] overflow-hidden bg-slate-950 flex items-center">
          {/* Background image */}
          <img
            src="https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=2000&q=85"
            alt="Live event"
            className="absolute inset-0 h-full w-full object-cover opacity-70"
          />
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

          {/* Orbs */}
          <div className="absolute top-1/4 left-1/4 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-[120px] pointer-events-none" />
          <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-orange-500/20 blur-[120px] pointer-events-none" />

          {/* Floating stat badges - Hidden on mobile, visible on lg */}
          <div className="reveal-on-scroll delay-400 float-card absolute left-8 xl:left-16 top-1/3 z-10 hidden lg:block">
            <div className="relative rounded-2xl border border-slate-700/50 bg-slate-900/60 px-6 py-5 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.5)] flex items-center gap-4 group hover:bg-slate-800/80 transition-colors">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-[0_0_15px_rgba(251,191,36,0.4)] text-slate-950 text-xl">
                <FiStar className="fill-slate-950" />
              </div>
              <div className="relative">
                <div className="text-2xl font-black text-white leading-none">4.9/5</div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-400 mt-1.5">Platform Rating</div>
              </div>
            </div>
          </div>

          <div className="reveal-on-scroll delay-500 float-card absolute right-8 xl:right-16 top-[45%] z-10 hidden lg:block" style={{ animationDelay: '1s' }}>
            <div className="relative rounded-2xl border border-slate-700/50 bg-slate-900/60 px-6 py-5 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.5)] flex items-center gap-4 group hover:bg-slate-800/80 transition-colors">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-bl from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-red-500 shadow-[0_0_15px_rgba(249,115,22,0.4)] text-white text-xl">
                <FiUsers />
              </div>
              <div className="relative">
                <div className="text-2xl font-black text-white leading-none">500+</div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-orange-400 mt-1.5">Active Organisers</div>
              </div>
            </div>
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20 flex flex-col items-center">
            <div className="max-w-4xl text-center flex flex-col items-center">
              {/* Badge */}
              <div className="reveal-on-scroll inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-5 py-2 backdrop-blur-sm mb-8">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-amber-300">ShowTimeBro for Organisers</span>
              </div>

              <h1 className="reveal-on-scroll delay-100 text-5xl font-black leading-[1.1] tracking-tight text-white sm:text-6xl lg:text-[5.5rem]">
                List Your{" "}
                <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">Show</span>
              </h1>

              <p className="reveal-on-scroll delay-200 mt-8 max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl">
                Reach your audience on ShowTimeBro. Set up your organiser account, create
                your event, and start selling tickets — all in minutes.
              </p>

              <div className="reveal-on-scroll delay-300 mt-12 flex flex-col sm:flex-row items-center gap-6">
                <button
                  type="button"
                  onClick={() => nav('/listyourshow/account-setup')}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-10 py-4 text-base font-extrabold text-slate-950 transition-all duration-300 hover:-translate-y-1 hover:from-amber-300 hover:to-amber-400 shadow-[0_0_40px_rgba(251,191,36,0.25)] hover:shadow-[0_15px_60px_-10px_rgba(251,191,36,0.5)]"
                >
                  List Your Show
                  <FiArrowRight className="text-lg transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
                <Link
                  to="/listyourshow/manage"
                  className="text-sm font-bold text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1 group"
                >
                  Already an organiser? <span className="underline underline-offset-4 group-hover:no-underline">Go to console</span> <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ─── HIGHLIGHTS ─── */}
        <section className="relative bg-[#fdfbf7] py-24 sm:py-32 overflow-hidden">
          {/* Soft white spotlight in the center for a premium 3D depth effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full bg-white/80 blur-[120px] pointer-events-none" />
          
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="reveal-on-scroll text-center mb-20">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-amber-600">Why ShowTimeBro</p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-stone-900 sm:text-5xl">
                Everything you need to sell out
              </h2>
            </div>
            <div className="grid gap-8 sm:grid-cols-3">
              {HIGHLIGHTS.map(({ icon: Icon, title, copy, gradient }, index) => (
                <div
                  key={title}
                  className={`reveal-on-scroll delay-${(index + 1) * 100} group relative overflow-hidden rounded-[2.5rem] border border-stone-200/80 bg-white/90 backdrop-blur-xl p-10 sm:p-12 shadow-xl shadow-stone-200/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-stone-200/80 hover:bg-white text-center flex flex-col items-center`}
                >
                  {/* Decorative ambient top glow */}
                  <div className={`absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-gradient-to-br ${gradient} rounded-full blur-3xl opacity-10 transition-opacity duration-500 group-hover:opacity-25 pointer-events-none`} />
                  
                  {/* Icon with glowing orb */}
                  <div className="relative mb-8 mt-2 inline-flex">
                    <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${gradient} blur-xl opacity-20 transition-opacity duration-500 group-hover:opacity-40`} />
                    <div className={`relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br ${gradient} text-3xl text-white shadow-xl ring-1 ring-white/50`}>
                      <Icon strokeWidth={2.5} />
                    </div>
                  </div>
                  
                  <h3 className="relative z-10 text-2xl font-black tracking-tight text-stone-900">{title}</h3>
                  <p className="relative z-10 mt-4 text-base font-medium leading-relaxed text-stone-600 max-w-[280px]">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── HOST TYPES ─── */}
        <section className="relative bg-slate-950 py-20 overflow-hidden">
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="reveal-on-scroll mx-auto max-w-2xl text-center mb-14">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-amber-500">
                Made for every kind of gathering
              </p>
              <h2 className="mt-3 text-4xl font-black tracking-tight text-white sm:text-5xl">
                What can you host?
              </h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {HOST_TYPES.map(({ name, copy, emoji }, index) => (
                <div
                  key={name}
                  className={`reveal-on-scroll delay-${(index % 6 + 1) * 100} group relative flex flex-col items-center rounded-3xl border border-white/5 bg-gradient-to-b from-white/[0.04] to-transparent px-3 py-6 text-center shadow-[0_8px_24px_rgba(0,0,0,0.4),0_4px_16px_rgba(245,158,11,0.03)] ring-1 ring-white/[0.02] transition-all duration-500 hover:-translate-y-2 hover:border-amber-500/30 hover:shadow-[0_10px_40px_-10px_rgba(245,158,11,0.25)]`}
                >
                  <div className="relative mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] ring-1 ring-white/10 transition-all duration-500 group-hover:scale-110 group-hover:bg-amber-500/10 group-hover:ring-amber-500/50">
                    <span className="relative z-10 text-3xl filter drop-shadow-md transition-transform duration-500 group-hover:scale-110">{emoji}</span>
                  </div>
                  
                  <h3 className="text-[14px] font-black tracking-wide text-white transition-colors duration-300 group-hover:text-amber-400">{name}</h3>
                  <p className="mt-2 hidden text-[11px] font-medium leading-relaxed text-slate-400 lg:block opacity-80">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="reveal-on-scroll mx-auto max-w-2xl text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Simple by design</p>
              <h2 className="mt-3 text-4xl font-black tracking-tight text-slate-900">How it works</h2>
            </div>

            <div className="relative grid gap-8 md:grid-cols-3">
              {/* Connector line */}
              <div className="reveal-on-scroll delay-200 absolute top-10 left-[16.66%] right-[16.66%] hidden h-px bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 md:block" />

              {STEPS.map(({ icon: Icon, title, copy, step }, index) => (
                <div key={title} className={`reveal-on-scroll delay-${(index + 1) * 100} group relative text-center`}>
                  <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 opacity-20 blur-xl group-hover:opacity-40 transition-opacity" />
                    <div className="relative flex h-full w-full items-center justify-center rounded-full border-2 border-amber-400 bg-white shadow-lg text-2xl text-amber-600 group-hover:bg-amber-50 transition-colors">
                      <Icon />
                    </div>
                    <span className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-[11px] font-black text-slate-950 shadow">
                      {step}
                    </span>
                  </div>
                  <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500 max-w-[220px] mx-auto">{copy}</p>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="relative reveal-on-scroll delay-200 mt-16 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-10 text-center shadow-2xl">
              <div className="absolute -inset-px rounded-3xl border border-white/5 pointer-events-none" />
              <div className="relative z-10">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Ready to get started?</p>
                <h3 className="mt-3 text-3xl font-black text-white">Start selling tickets today</h3>
                <p className="mt-3 text-slate-400 max-w-md mx-auto text-sm">
                  Join hundreds of organisers already growing their events on ShowTimeBro.
                </p>
                <button
                  type="button"
                  onClick={() => nav('/listyourshow/account-setup')}
                  className="relative z-10 group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-10 py-4 text-base font-extrabold text-slate-950 transition-all duration-300 hover:-translate-y-1 hover:from-amber-300 hover:to-amber-400 shadow-[0_0_40px_rgba(251,191,36,0.25)] hover:shadow-[0_15px_60px_-10px_rgba(251,191,36,0.5)] cursor-pointer"
                >
                  List Your Show
                  <FiArrowRight className="text-lg transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FOOTER BAR ─── */}
        <div className="reveal-on-scroll border-t border-slate-100 bg-white py-6 text-center text-xs text-slate-500">
          Already an organiser?{" "}
          <Link to="/listyourshow/manage" className="font-bold text-amber-700 hover:text-amber-600 hover:underline transition-colors">
            Go to your console →
          </Link>
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab="login"
        next={goAfterAuth}
        logoSrc={logo}
        brandName="Show Time Bro"
        headerVariant="logo"
      />
    </>
  );
}

